"use client";

import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { CheckCircle2Icon, Loader2Icon, MailIcon } from "lucide-react";
import { useId, useState } from "react";
import { PromptGeneratorApiError, emailGenerationResults } from "../lib/api";
import { EMAIL_PANEL } from "../lib/copy";

type EmailFieldState =
	| "idle"
	| "submitting"
	| "sent"
	| "error"
	| "already_sent";

/**
 * "Email me this panel": optional, one email per generation, and the results
 * stay ungated. The note under the field is the whole promise: we won't add
 * the address to an email list, and they can regenerate any time with the
 * same domain. Nothing here reads as an opt-in, because it isn't one.
 *
 * The address lives only in this component's state and the one request that
 * sends it; the server keeps a salted hash for the limit, so a later visit
 * (or a 409) shows "already sent" without the address. The error line uses
 * the error Badge rather than --destructive, which is reserved for
 * destructive actions.
 */
export function EmailPanelField({
	generationId,
	alreadySent,
	onSent,
}: {
	generationId: string;
	/** The server says this generation's one email already went out. */
	alreadySent: boolean;
	onSent?: () => void;
}) {
	const inputId = useId();
	const helpId = useId();
	const [state, setState] = useState<EmailFieldState>(
		alreadySent ? "already_sent" : "idle",
	);
	const [address, setAddress] = useState("");
	const [sentTo, setSentTo] = useState<string | null>(null);
	const sending = state === "submitting";
	const showForm =
		state === "idle" || state === "submitting" || state === "error";

	const submit = async () => {
		const email = address.trim();
		if (!email) {
			setState("error");
			return;
		}
		setState("submitting");
		try {
			await emailGenerationResults(generationId, email);
			setSentTo(email);
			setAddress("");
			setState("sent");
			onSent?.();
		} catch (error) {
			setState(
				error instanceof PromptGeneratorApiError && error.status === 409
					? "already_sent"
					: "error",
			);
		}
	};

	// `ph-no-capture`: PostHog's autocapture and session replay skip this
	// subtree, so the typed address (and the "Sent to" line) never reach
	// analytics.
	return (
		<div className="ph-no-capture space-y-2 rounded-xl border bg-muted/30 p-4">
			<div className="flex items-center gap-2">
				<MailIcon
					className="size-4 shrink-0 text-muted-foreground"
					aria-hidden
				/>
				<label htmlFor={inputId} className="text-sm font-semibold">
					{EMAIL_PANEL.fieldLabel}{" "}
					<span className="font-normal text-muted-foreground">
						{EMAIL_PANEL.optional}
					</span>
				</label>
			</div>
			{showForm ? (
				<div
					id={helpId}
					className="space-y-1 text-xs text-muted-foreground"
				>
					<p>{EMAIL_PANEL.intro}</p>
					<p>{EMAIL_PANEL.privacyNote}</p>
				</div>
			) : null}

			{showForm ? (
				<form
					className="flex flex-col gap-2 sm:flex-row"
					onSubmit={(e) => {
						e.preventDefault();
						void submit();
					}}
				>
					<Input
						id={inputId}
						type="email"
						autoComplete="email"
						required
						placeholder={EMAIL_PANEL.placeholder}
						value={address}
						onChange={(e) => {
							setAddress(e.target.value);
							if (state === "error") {
								setState("idle");
							}
						}}
						aria-describedby={helpId}
						aria-invalid={state === "error" || undefined}
						disabled={sending}
						className="min-w-0 flex-1 bg-background text-sm"
					/>
					<Button
						type="submit"
						variant="outline"
						disabled={sending}
						className="shrink-0"
					>
						{sending ? (
							<>
								<Loader2Icon className="size-4 animate-spin motion-reduce:animate-none" />
								{EMAIL_PANEL.sending}
							</>
						) : (
							EMAIL_PANEL.submit
						)}
					</Button>
				</form>
			) : null}

			<div aria-live="polite">
				{state === "error" ? (
					<p className="flex flex-wrap items-center gap-2 text-sm">
						<Badge
							status="error"
							className="px-2 py-0.5 text-[10px]"
						>
							{EMAIL_PANEL.errorBadge}
						</Badge>
						<span className="text-foreground">
							{EMAIL_PANEL.errorMessage}
						</span>
					</p>
				) : null}
				{state === "sent" ? (
					<div className="space-y-1">
						<p className="flex items-start gap-2 text-sm">
							<CheckCircle2Icon
								className="mt-0.5 size-4 shrink-0 text-primary"
								aria-hidden
							/>
							<span className="text-foreground">
								{EMAIL_PANEL.sentMessage}
							</span>
						</p>
						{sentTo ? (
							<p className="pl-6 text-xs text-muted-foreground">
								{EMAIL_PANEL.sentTo(sentTo)}
							</p>
						) : null}
					</div>
				) : null}
				{state === "already_sent" ? (
					<p className="flex flex-wrap items-center gap-2 text-sm">
						<Badge
							status="info"
							className="px-2 py-0.5 text-[10px]"
						>
							{EMAIL_PANEL.alreadySentBadge}
						</Badge>
						<span className="text-foreground">
							{EMAIL_PANEL.alreadySentMessage}
						</span>
					</p>
				) : null}
			</div>
		</div>
	);
}
